import { ref, watch, computed } from 'vue'

export type Theme = 'light' | 'dark'

// Initialize theme immediately from localStorage or system preference
const getInitialTheme = (): Theme => {
  if (typeof window === 'undefined') return 'light'
  
  const savedTheme = localStorage.getItem('app-theme') as Theme | null
  if (savedTheme) {
    return savedTheme
  }
  
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark'
  }
  
  return 'light'
}

const theme = ref<Theme>(getInitialTheme())

// Apply theme immediately
const applyTheme = (newTheme: Theme) => {
  if (newTheme === 'dark') {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
  localStorage.setItem('app-theme', newTheme)
}

// Apply initial theme
applyTheme(theme.value)

export function useTheme() {
  const isDark = computed(() => theme.value === 'dark')

  const toggleTheme = () => {
    theme.value = theme.value === 'light' ? 'dark' : 'light'
  }

  const setTheme = (newTheme: Theme) => {
    theme.value = newTheme
  }

  // Watch for theme changes and update DOM and localStorage
  watch(theme, (newTheme) => {
    applyTheme(newTheme)
  })

  return {
    theme,
    isDark,
    toggleTheme,
    setTheme
  }
}
